/**
 * Partner Portal
 * Public-facing portal for partners to view invoices and manage payments
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { formatCurrency, formatDate, isOverdue, daysDifference } from '../utils/formatters';
import {
  getPortalDashboard,
  getPortalInvoices,
  processManualPayment,
  selectPrepayOption,
  submitGcashPaymentClaim,
  submitMonthlyProposal,
  getStoredSession,
  portalLogout,
} from '../services/portalService';
import { ContractEquivalent } from '../components/ContractEquivalent';
import './PartnerPortal.css';

const PartnerPortal = () => {
  const { accessToken } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [client, setClient] = useState(null);
  const [projects, setProjects] = useState([]);
  const [permissions, setPermissions] = useState({});
  const [invoices, setInvoices] = useState([]);
  const [costBreakdown, setCostBreakdown] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [manualPayment, setManualPayment] = useState(null);
  const [flexibleMonthly, setFlexibleMonthly] = useState(null);
  const [monthlyProposal, setMonthlyProposal] = useState(null);
  const [flexibleDevFee, setFlexibleDevFee] = useState(null);
  const [devFeeProposal, setDevFeeProposal] = useState(null);
  const [activeTab, setActiveTab] = useState('invoices');
  const [processingPayment, setProcessingPayment] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Plan Choice (prepay) state — only populated for clients on a plan with
  // prepayOptions and only meaningful while prepayChoiceRequired is true.
  const [prepayChoiceRequired, setPrepayChoiceRequired] = useState(false);
  const [offeredPrepayOptions, setOfferedPrepayOptions] = useState(null);
  const [currentPrepayChoice, setCurrentPrepayChoice] = useState(null);
  const [planChoiceTitle, setPlanChoiceTitle] = useState('Plan Choice');
  const [planChoiceIntro, setPlanChoiceIntro] = useState('');
  const [planChoiceNote, setPlanChoiceNote] = useState('');
  const [pickingPlanKey, setPickingPlanKey] = useState(null);
  const [planChoiceError, setPlanChoiceError] = useState(null);
  const [planChoiceNotice, setPlanChoiceNotice] = useState(null);

  // Single-call portal load: session check + access validation + all data
  useEffect(() => {
    const initPortal = async () => {
      try {
        setLoading(true);
        setError(null);

        const session = getStoredSession();
        if (!session?.sessionToken || session?.accessToken !== accessToken) {
          navigate(`/portal/${accessToken}/login`, { replace: true });
          return;
        }

        const result = await getPortalDashboard(session.sessionToken, accessToken);

        if (result.needsAuth) {
          navigate(`/portal/${accessToken}/login`, { replace: true });
          return;
        }

        setIsAuthenticated(true);
        setClient(result.client);
        setProjects(result.projects || []);
        setPermissions(result.permissions);
        setInvoices(result.invoices || []);
        setCostBreakdown(result.costBreakdown || []);
        setPaymentMethods(result.paymentMethods || []);
        setManualPayment(result.manualPayment || null);
        setFlexibleMonthly(result.flexibleMonthly || null);
        setMonthlyProposal(result.monthlyProposal || null);
        setFlexibleDevFee(result.flexibleDevFee || null);
        setDevFeeProposal(result.devFeeProposal || null);
        setPrepayChoiceRequired(!!result.prepayChoiceRequired);
        setOfferedPrepayOptions(result.offeredPrepayOptions || null);
        setCurrentPrepayChoice(result.currentPrepayChoice || null);
        setPlanChoiceTitle(result.planChoiceTitle || 'Plan Choice');
        setPlanChoiceIntro(result.planChoiceIntro || '');
        setPlanChoiceNote(result.planChoiceNote || '');

        // Land on Plan Choice (not an empty "No invoices yet") when the client
        // has a plan to pick and nothing to pay yet — avoids a confusing empty
        // first impression that could make them hesitate to set up payment.
        const hasInvoices = (result.invoices || []).length > 0;
        const hasPlanToPick = result.flexibleMonthly || result.flexibleDevFee || result.offeredPrepayOptions || result.currentPrepayChoice;
        if (result.prepayChoiceRequired || (!hasInvoices && hasPlanToPick)) {
          setActiveTab('plan');
        }

        setLoading(false);
      } catch (err) {
        console.error('Portal init error:', err);
        setError('Invalid or expired access link. Please contact support for a new link.');
        setLoading(false);
      }
    };

    if (accessToken) {
      initPortal();
    } else {
      setError('No access token provided');
      setLoading(false);
    }
  }, [accessToken, navigate]);

  const handleLogout = async () => {
    await portalLogout();
    navigate(`/portal/${accessToken}/login`, { replace: true });
  };

  const handleGcashClaim = async (invoice) => {
    const remaining = (invoice.totalAmount || 0) - (invoice.paidAmount || 0);
    const amtStr = window.prompt(
      `Report your GCash payment for ${invoice.invoiceNumber}.\n\nHow much did you send? ` +
      `(balance due: ${formatCurrency(remaining, invoice.currency)})`,
      String(remaining),
    );
    if (amtStr === null) return;
    const amount = parseFloat(amtStr);
    if (!(amount > 0)) { window.alert('Please enter a valid amount.'); return; }
    const reference = window.prompt('Enter the GCash reference number from your receipt:');
    if (!reference || !reference.trim()) return;
    setProcessingPayment(invoice.id);
    try {
      await submitGcashPaymentClaim(accessToken, invoice.id, reference.trim(), amount);
      window.alert('Thanks! We\'ve received your payment report and will confirm once it reflects in our GCash. 🙏');
      // Refresh so the invoice shows "pending verification"
      const session = getStoredSession();
      const result = await getPortalDashboard(session.sessionToken, accessToken);
      if (!result.needsAuth) setInvoices(result.invoices);
    } catch (e) {
      window.alert(e.message || 'Could not submit. Please try again.');
    } finally {
      setProcessingPayment(null);
    }
  };

  const handleProposeAmount = async (kind) => {
    const isDevFee = kind === 'devFee';
    const cfg = isDevFee ? flexibleDevFee : flexibleMonthly;
    const cur = cfg?.currency || 'CAD';
    const min = Number(cfg?.min) || 0;
    const suggested = cfg?.suggested || min || '';
    const noun = isDevFee ? 'one-time development fee' : 'monthly amount';
    const suffix = isDevFee ? '' : '/month';
    const amtStr = window.prompt(
      `Set the ${noun} you'd like to pay${min ? ` (minimum ${cur} ${min.toLocaleString()})` : ''}:`,
      String(suggested),
    );
    if (amtStr === null) return;
    const amount = parseFloat(amtStr);
    if (!(amount > 0)) { window.alert('Please enter a valid amount.'); return; }
    if (min && amount < min) { window.alert(`The minimum is ${cur} ${min.toLocaleString()}${suffix}.`); return; }
    try {
      await submitMonthlyProposal(accessToken, amount, kind);
      window.alert(`Thanks! Your proposed ${noun} has been sent to us for confirmation. 🙏`);
      const session = getStoredSession();
      const result = await getPortalDashboard(session.sessionToken, accessToken);
      if (!result.needsAuth) {
        setMonthlyProposal(result.monthlyProposal || null);
        setDevFeeProposal(result.devFeeProposal || null);
      }
    } catch (e) {
      window.alert(e.message || 'Could not submit. Please try again.');
    }
  };
  const handleProposeMonthly = () => handleProposeAmount('monthly');
  const handleProposeDevFee = () => handleProposeAmount('devFee');

  const handlePayNow = (invoice) => {
    if (!paymentMethods.length) {
      navigate(`/portal/${accessToken}/payment-setup`);
      return;
    }

    // Pre-select the default payment method
    const defaultMethod = paymentMethods.find(pm => pm.isDefault) || paymentMethods[0];
    setSelectedPaymentMethod(defaultMethod.id);
    setSelectedInvoice(invoice);
    setShowPaymentModal(true);
  };

  const handleConfirmPayment = async () => {
    if (!selectedPaymentMethod || !selectedInvoice) {
      return;
    }

    setShowPaymentModal(false);
    setProcessingPayment(selectedInvoice.id);

    try {
      const result = await processManualPayment(accessToken, selectedInvoice.id, selectedPaymentMethod);
      if (result.success) {
        // Reload invoices
        const invoicesResult = await getPortalInvoices(accessToken);
        setInvoices(invoicesResult.invoices || []);
        alert('Payment successful!');
      } else {
        alert('Payment failed. Please try again or contact support.');
      }
    } catch (err) {
      alert('Payment error: ' + err.message);
    } finally {
      setProcessingPayment(null);
      setSelectedInvoice(null);
    }
  };

  const handlePickPlan = async (optionKey) => {
    if (pickingPlanKey) return;
    setPlanChoiceError(null);
    setPlanChoiceNotice(null);
    setPickingPlanKey(optionKey);
    try {
      const result = await selectPrepayOption(accessToken, optionKey);
      // Server returns the post-write prepay payload — drive the UI off it.
      setPrepayChoiceRequired(!!result.prepayChoiceRequired);
      setOfferedPrepayOptions(result.offeredPrepayOptions || null);
      setCurrentPrepayChoice(result.currentPrepayChoice || null);
      if (result.planChoiceTitle) setPlanChoiceTitle(result.planChoiceTitle);
      if (result.planChoiceIntro) setPlanChoiceIntro(result.planChoiceIntro);
      if ('planChoiceNote' in result) setPlanChoiceNote(result.planChoiceNote || '');
      setPlanChoiceNotice(
        result.alreadyChosen
          ? 'You already chose this plan — nothing changed.'
          : result.prepayInvoiceNumber
            ? `Prepay invoice ${result.prepayInvoiceNumber} is ready in your Invoices tab.`
            : 'Plan locked in. Monthly Workspace + domain will start at handover.',
      );
      // Refresh invoices so a newly-created prepay invoice shows up.
      const invoicesResult = await getPortalInvoices(accessToken);
      setInvoices(invoicesResult.invoices || []);
    } catch (err) {
      setPlanChoiceError(err.message || 'Could not save your choice — please try again.');
    } finally {
      setPickingPlanKey(null);
    }
  };

  const getBrandIcon = (brand) => {
    const icons = {
      visa: 'VISA',
      mastercard: 'MC',
      amex: 'AMEX',
      discover: 'DISC',
    };
    return icons[brand?.toLowerCase()] || 'CARD';
  };

  const getStatusBadge = (invoice) => {
    if (invoice.status === 'paid') {
      return <span className="badge badge-success">Paid</span>;
    }
    if (invoice.status === 'partial') {
      return <span className="badge badge-info">Partial</span>;
    }
    if (isOverdue(invoice.dueDate)) {
      return <span className="badge badge-danger">Overdue</span>;
    }
    return <span className="badge badge-warning">Pending</span>;
  };

  if (loading) {
    return (
      <div className="portal-container">
        <div className="portal-loading">
          <div className="spinner"></div>
          <p>Loading portal...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="portal-container">
        <div className="portal-error">
          <h2>Access Denied</h2>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  // Only surface the Cost Breakdown tab when there's actual usage to show.
  // Flat-fee / all-in clients (no GCP usage billing) don't get a noisy empty tab.
  const hasCostData = costBreakdown.some(
    (p) => p.totalCost > 0 || (p.services && p.services.length > 0),
  );

  return (
    <div className="portal-container">
      <header className="portal-header">
        <div className="portal-brand">
          <h1>FusionICT Billing</h1>
          <span className="portal-label">Partner Portal</span>
        </div>
        <div className="portal-client-info">
          <div className="client-details">
            <span className="client-name">{client?.name}</span>
            <span className="client-email">{client?.email}</span>
          </div>
          <button className="logout-btn" onClick={handleLogout} title="Sign out">
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
              <polyline points="16 17 21 12 16 7"/>
              <line x1="21" y1="12" x2="9" y2="12"/>
            </svg>
          </button>
        </div>
      </header>

      <nav className="portal-nav">
        {(offeredPrepayOptions || currentPrepayChoice || flexibleMonthly || flexibleDevFee) && (
          <button
            className={`nav-tab ${activeTab === 'plan' ? 'active' : ''}`}
            onClick={() => setActiveTab('plan')}
          >
            Plan Choice
            {prepayChoiceRequired && <span className="nav-tab-dot" aria-hidden>•</span>}
          </button>
        )}
        <button
          className={`nav-tab ${activeTab === 'invoices' ? 'active' : ''}`}
          onClick={() => setActiveTab('invoices')}
        >
          Invoices
        </button>
        {hasCostData && (
          <button
            className={`nav-tab ${activeTab === 'costs' ? 'active' : ''}`}
            onClick={() => setActiveTab('costs')}
          >
            Cost Breakdown
          </button>
        )}
        {permissions.canUpdatePayment && (
          <button
            className={`nav-tab ${activeTab === 'payment' ? 'active' : ''}`}
            onClick={() => setActiveTab('payment')}
          >
            Payment Methods
          </button>
        )}
      </nav>

      <main className="portal-main">
        {activeTab === 'plan' && flexibleMonthly && (
          <div className="portal-section">
            <h2>Choose your monthly plan</h2>
            <p className="plan-choice-intro">
              Set the monthly amount you&apos;d like to pay for your website&apos;s hosting, maintenance
              &amp; support. Pick what works for you{Number(flexibleMonthly.min) > 0
                ? ` (minimum ${flexibleMonthly.currency || 'CAD'} ${Number(flexibleMonthly.min).toLocaleString()}/mo)`
                : ''} — we&apos;ll confirm it and set up your monthly billing.
            </p>

            {monthlyProposal ? (
              <div style={{
                margin: '0 0 16px', padding: '14px 16px', borderRadius: '8px',
                background: monthlyProposal.status === 'approved' ? '#f0fdf4' : '#fffbeb',
                border: `1px solid ${monthlyProposal.status === 'approved' ? '#bbf7d0' : '#fcd34d'}`,
                color: monthlyProposal.status === 'approved' ? '#166534' : '#78350f',
              }}>
                {monthlyProposal.status === 'approved'
                  ? `✅ Your monthly plan is active: ${monthlyProposal.currency} ${Number(monthlyProposal.amount).toLocaleString()}/month.`
                  : `⏳ You proposed ${monthlyProposal.currency} ${Number(monthlyProposal.amount).toLocaleString()}/month — pending our confirmation.`}
                <div style={{ marginTop: 10 }}>
                  <button className="btn-sm btn-secondary" onClick={handleProposeMonthly}>
                    {monthlyProposal.status === 'approved' ? 'Change amount' : 'Change proposal'}
                  </button>
                </div>
              </div>
            ) : (
              <div style={{
                margin: '0 0 16px', padding: '16px', borderRadius: '8px',
                background: '#eff6ff', border: '1px solid #93c5fd',
              }}>
                <button className="btn btn-primary" onClick={handleProposeMonthly}>
                  Set my monthly amount
                </button>
              </div>
            )}
          </div>
        )}
        {activeTab === 'plan' && flexibleDevFee && (
          <div className="portal-section">
            <h2>One-time development fee</h2>
            <p className="plan-choice-intro">
              This is a one-time fee for building your website. If you&apos;d like, you can propose
              the amount you&apos;re comfortable paying{Number(flexibleDevFee.min) > 0
                ? ` (minimum ${flexibleDevFee.currency || 'CAD'} ${Number(flexibleDevFee.min).toLocaleString()})`
                : ''} — we&apos;ll confirm it and send you an invoice.
            </p>

            {devFeeProposal ? (
              <div style={{
                margin: '0 0 16px', padding: '14px 16px', borderRadius: '8px',
                background: devFeeProposal.status === 'approved' ? '#f0fdf4' : '#fffbeb',
                border: `1px solid ${devFeeProposal.status === 'approved' ? '#bbf7d0' : '#fcd34d'}`,
                color: devFeeProposal.status === 'approved' ? '#166534' : '#78350f',
              }}>
                {devFeeProposal.status === 'approved'
                  ? `✅ Your development fee is confirmed: ${devFeeProposal.currency} ${Number(devFeeProposal.amount).toLocaleString()}.`
                  : `⏳ You proposed ${devFeeProposal.currency} ${Number(devFeeProposal.amount).toLocaleString()} — pending our confirmation.`}
                {devFeeProposal.status !== 'approved' && (
                  <div style={{ marginTop: 10 }}>
                    <button className="btn-sm btn-secondary" onClick={handleProposeDevFee}>
                      Change proposal
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div style={{
                margin: '0 0 16px', padding: '16px', borderRadius: '8px',
                background: '#eff6ff', border: '1px solid #93c5fd',
              }}>
                <button className="btn btn-primary" onClick={handleProposeDevFee}>
                  Propose my development fee
                </button>
              </div>
            )}
          </div>
        )}
        {activeTab === 'plan' && (offeredPrepayOptions || currentPrepayChoice) && (
          <div className="portal-section">
            <h2>{planChoiceTitle}</h2>
            {planChoiceIntro && <p className="plan-choice-intro">{planChoiceIntro}</p>}
            {planChoiceNote && (
              <div className="plan-choice-note" style={{
                background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: '8px',
                padding: '12px 14px', margin: '0 0 16px', fontSize: '0.85rem', color: '#78350f',
              }}>
                {planChoiceNote}
              </div>
            )}

            {currentPrepayChoice && !prepayChoiceRequired && (
              <div className="plan-choice-current">
                <h3>Your current plan</h3>
                <p>
                  <strong>{currentPrepayChoice.label || currentPrepayChoice.key}</strong>
                </p>
                {currentPrepayChoice.shape === 'prepay' && currentPrepayChoice.expiresAt && (
                  <p className="text-small">
                    Coverage runs through {formatDate(currentPrepayChoice.expiresAt)}.
                    {currentPrepayChoice.prepayInvoiceNumber && (
                      <>
                        {' '}Prepay invoice:{' '}
                        <span className="font-mono">{currentPrepayChoice.prepayInvoiceNumber}</span>
                      </>
                    )}
                  </p>
                )}
                {currentPrepayChoice.shape === 'subscription' && (
                  <p className="text-small">
                    Billed monthly — cancel any time.
                  </p>
                )}
                <p className="text-small">
                  Want to switch plans? Contact us — we&apos;ll figure out pro-ration.
                </p>
              </div>
            )}

            {planChoiceError && (
              <div className="plan-choice-error">{planChoiceError}</div>
            )}
            {planChoiceNotice && (
              <div className="plan-choice-notice">{planChoiceNotice}</div>
            )}

            {!currentPrepayChoice && Array.isArray(offeredPrepayOptions) && (
              <div className="plan-choice-grid">
                {offeredPrepayOptions.map((opt) => {
                  const isBusy = pickingPlanKey === opt.key;
                  const showPrice = opt.shape === 'prepay';
                  return (
                    <div
                      key={opt.key}
                      className={`plan-choice-card ${opt.isDefault ? 'default' : ''}`}
                    >
                      <div className="plan-choice-card-header">
                        <h3>{opt.label}</h3>
                        {opt.isDefault && (
                          <span className="plan-choice-badge">Recommended</span>
                        )}
                      </div>
                      {showPrice && (
                        <div className="plan-choice-price">
                          {formatCurrency(opt.upfrontAmount, opt.currency || 'USD')}
                          <span className="plan-choice-price-suffix">
                            {opt.durationYears > 1 ? ` total / ${opt.durationYears} years` : ' total / 1 year'}
                          </span>
                        </div>
                      )}
                      <p className="plan-choice-description">{opt.description}</p>
                      <button
                        type="button"
                        className={`btn ${opt.isDefault ? 'btn-secondary' : 'btn-primary'} plan-choice-cta`}
                        onClick={() => handlePickPlan(opt.key)}
                        disabled={!!pickingPlanKey}
                      >
                        {isBusy ? 'Saving…' : 'Choose this plan'}
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {activeTab === 'invoices' && (
          <div className="portal-section">
            <h2>Your Invoices</h2>
            {invoices.length === 0 ? (
              <div className="empty-state">
                <p>No invoices yet.</p>
              </div>
            ) : (
              <div className="invoice-list">
                <table className="portal-table">
                  <thead>
                    <tr>
                      <th>Invoice #</th>
                      <th>Project</th>
                      <th>Period</th>
                      <th>Issue Date</th>
                      <th>Due Date</th>
                      <th>Amount</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoices.map(invoice => (
                      <tr key={invoice.id}>
                        <td className="font-mono">{invoice.invoiceNumber}</td>
                        <td>{invoice.projectName}</td>
                        <td>{invoice.billingPeriod}</td>
                        <td>{formatDate(invoice.issueDate)}</td>
                        <td>
                          {formatDate(invoice.dueDate)}
                          {invoice.status !== 'paid' && (
                            <div className="due-info">
                              {isOverdue(invoice.dueDate)
                                ? `${Math.abs(daysDifference(invoice.dueDate))} days overdue`
                                : `Due in ${daysDifference(invoice.dueDate)} days`
                              }
                            </div>
                          )}
                        </td>
                        <td className="amount">
                          {invoice.paidAmount > 0 && invoice.status !== 'paid' ? (
                            <>
                              {formatCurrency(invoice.totalAmount - invoice.paidAmount, invoice.currency)}
                              <div className="due-info">
                                of {formatCurrency(invoice.totalAmount, invoice.currency)} · {formatCurrency(invoice.paidAmount, invoice.currency)} paid
                              </div>
                            </>
                          ) : (
                            formatCurrency(invoice.totalAmount, invoice.currency)
                          )}
                          <ContractEquivalent invoice={invoice} />
                        </td>
                        <td>{getStatusBadge(invoice)}</td>
                        <td>
                          <div className="action-buttons">
                            <button
                              className="btn-sm btn-secondary"
                              onClick={() => navigate(`/portal/${accessToken}/invoice/${invoice.id}`)}
                            >
                              View
                            </button>
                            {invoice.status !== 'paid' && invoice.paymentClaim?.status === 'pending' ? (
                              <span
                                title={invoice.paymentClaim.reference ? `GCash ref: ${invoice.paymentClaim.reference}` : ''}
                                style={{ fontSize: '0.8rem', color: '#92400e', background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: '6px', padding: '4px 8px', whiteSpace: 'nowrap' }}
                              >
                                ⏳ Payment reported — verifying
                              </span>
                            ) : invoice.status !== 'paid' && (
                              <>
                                <button
                                  className="btn-sm btn-primary"
                                  onClick={() => handlePayNow(invoice)}
                                  disabled={processingPayment === invoice.id}
                                >
                                  {processingPayment === invoice.id ? 'Processing...' : 'Pay Now'}
                                </button>
                                <button
                                  className="btn-sm btn-secondary"
                                  onClick={() => handleGcashClaim(invoice)}
                                  disabled={processingPayment === invoice.id}
                                  title="Already paid via GCash? Report it here"
                                >
                                  I&apos;ve paid (GCash)
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {activeTab === 'costs' && hasCostData && (
          <div className="portal-section">
            <h2>Current Month Cost Breakdown</h2>
            {costBreakdown.length === 0 ? (
              <div className="empty-state">
                <p>No cost data available yet.</p>
              </div>
            ) : (
              <div className="cost-breakdown">
                {costBreakdown.map(project => (
                  <div key={project.projectId} className="project-costs card">
                    <div className="project-header">
                      <h3>{project.projectName}</h3>
                      <span className="period">{project.billingPeriod}</span>
                    </div>
                    <div className="total-cost">
                      <span>Total:</span>
                      <strong>{formatCurrency(project.totalCost)}</strong>
                    </div>
                    {project.services.length > 0 && (
                      <div className="services-list">
                        <h4>By Service</h4>
                        <table className="services-table">
                          <tbody>
                            {project.services.map((service, idx) => (
                              <tr key={idx}>
                                <td>{service.name}</td>
                                <td className="amount">{formatCurrency(service.amount)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'payment' && permissions.canUpdatePayment && (
          <div className="portal-section">
            <div className="section-header">
              <h2>Payment Methods</h2>
              <button
                className="btn btn-primary"
                onClick={() => navigate(`/portal/${accessToken}/payment-setup`)}
              >
                + Add Payment Method
              </button>
            </div>

            {paymentMethods.length === 0 ? (
              <div className="empty-state">
                <p>No payment methods on file.</p>
                <p className="text-small">Add a card to enable automatic payments.</p>
              </div>
            ) : (
              <div className="payment-methods-list">
                {paymentMethods.map(pm => (
                  <div key={pm.id} className={`payment-method-card ${pm.isDefault ? 'default' : ''}`}>
                    <div className="card-brand">{pm.brand.toUpperCase()}</div>
                    <div className="card-number">**** **** **** {pm.last4}</div>
                    <div className="card-expiry">Expires {pm.expMonth}/{pm.expYear}</div>
                    {pm.isDefault && <span className="default-badge">Default</span>}
                  </div>
                ))}
              </div>
            )}

            <div style={{
              margin: '16px 0 0', padding: '12px 14px', background: '#f0fdf4',
              border: '1px solid #bbf7d0', borderRadius: '8px', color: '#166534', fontSize: '0.85rem',
            }}>
              🔒 Your card is processed securely by Stripe (PCI-DSS Level 1). We never see or store your full card number — only Stripe does.
            </div>

            {manualPayment && manualPayment.enabled && (
              <div style={{
                margin: '16px 0 0', padding: '14px 16px', background: '#eff6ff',
                border: '1px solid #93c5fd', borderRadius: '8px',
              }}>
                <h3 style={{ margin: '0 0 8px', fontSize: '1rem', color: '#1e40af' }}>
                  Or pay via {manualPayment.label || 'GCash'}
                </h3>
                <p style={{ margin: '0 0 8px', fontSize: '0.9rem', color: '#1f2937' }}>
                  <strong>{manualPayment.accountName}</strong><br />
                  <span style={{ fontFamily: 'ui-monospace, SFMono-Regular, monospace', fontSize: '1.15rem', letterSpacing: '0.5px' }}>
                    {manualPayment.accountNumber}
                  </span>
                </p>
                {manualPayment.note && (
                  <p style={{ margin: 0, fontSize: '0.8rem', color: '#475569' }}>{manualPayment.note}</p>
                )}
              </div>
            )}

            <div className="auto-charge-section">
              <h3>Auto-Pay Settings</h3>
              <p className="text-small">
                {client?.autoPayEnabled
                  ? 'Auto-pay is enabled. Your monthly invoices are charged automatically to your default card.'
                  : client?.hasAutoChargePlan
                    ? 'You have a recurring monthly plan. Add a card above and it will be charged automatically each month.'
                    : 'Auto-pay is disabled. You will need to manually pay each invoice.'}
              </p>
              {(client?.autoPayEnabled || client?.hasAutoChargePlan) && (
                <div className="project-auto-charge">
                  <span>Monthly retainer</span>
                  <span className={`status ${client?.autoPayEnabled ? 'enabled' : 'disabled'}`}>
                    {client?.autoPayEnabled ? 'Auto-pay ON' : 'Awaiting card'}
                  </span>
                </div>
              )}
              {projects.filter(p => p.billingEnabled).map(project => (
                <div key={project.id} className="project-auto-charge">
                  <span>{project.gcpProjectName}</span>
                  <span className={`status ${project.autoChargeEnabled ? 'enabled' : 'disabled'}`}>
                    {project.autoChargeEnabled ? 'Auto-pay ON' : 'Auto-pay OFF'}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>

      <footer className="portal-footer">
        <p>Powered by <a href="https://fusionict.com" target="_blank" rel="noopener noreferrer">FusionICT</a></p>
      </footer>

      {/* Payment Method Selection Modal */}
      {showPaymentModal && selectedInvoice && (
        <div className="payment-modal-overlay" onClick={() => setShowPaymentModal(false)}>
          <div className="payment-modal" onClick={(e) => e.stopPropagation()}>
            <div className="payment-modal-header">
              <h3>Select Payment Method</h3>
              <button className="modal-close" onClick={() => setShowPaymentModal(false)}>&times;</button>
            </div>

            <div className="payment-modal-body">
              <p className="payment-amount">
                Amount to pay: <strong>{formatCurrency(selectedInvoice.totalAmount, selectedInvoice.currency)}</strong>
              </p>
              <ContractEquivalent invoice={selectedInvoice} className="modal centered" />
              <p className="payment-invoice-info">
                Invoice: {selectedInvoice.invoiceNumber}
              </p>

              <div className="payment-methods-select">
                {paymentMethods.map((pm) => (
                  <label
                    key={pm.id}
                    className={`payment-method-option ${selectedPaymentMethod === pm.id ? 'selected' : ''}`}
                  >
                    <input
                      type="radio"
                      name="paymentMethod"
                      value={pm.id}
                      checked={selectedPaymentMethod === pm.id}
                      onChange={() => setSelectedPaymentMethod(pm.id)}
                    />
                    <div className="payment-method-details">
                      <span className="card-brand-tag">{getBrandIcon(pm.brand)}</span>
                      <span className="card-number-display">•••• {pm.last4}</span>
                      <span className="card-expiry-display">{pm.expMonth?.toString().padStart(2, '0')}/{pm.expYear?.toString().slice(-2)}</span>
                      {pm.isDefault && <span className="default-tag">Default</span>}
                    </div>
                  </label>
                ))}
              </div>

              <button
                className="btn-link add-card-link"
                onClick={() => navigate(`/portal/${accessToken}/payment-setup`)}
              >
                + Add a new card
              </button>
            </div>

            <div className="payment-modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => setShowPaymentModal(false)}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleConfirmPayment}
                disabled={!selectedPaymentMethod}
              >
                Pay {formatCurrency(selectedInvoice.totalAmount, selectedInvoice.currency)}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PartnerPortal;
